/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useState} from 'react'
import PropTypes from 'prop-types'
import {Controller} from 'react-hook-form'
import {
    FormControl,
    FormLabel,
    FormErrorMessage,
    Input,
    InputGroup,
    Select,
    Checkbox,
    Image,
    useStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import useTagManager from '../../hooks/use-tag-manager'
import {getUrl} from '../../utils/cloudinary-utils'
import {Textarea} from '@chakra-ui/react'
import AddressSuggestions from '../address-suggestions'
import {formatAddress} from '../../utils/form-utils'
import debounce from 'lodash/debounce'
import useLoqate from '../../hooks/use-loqate'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
const DEBOUNCE_WAIT = 300

const Field = ({
    name,
    label,
    formLabel,
    form,
    type = 'text',
    options = [],
    rules = {},
    error,
    placeholder,
    inputProps,
    control,
    autoComplete,
    defaultValue,
    triggerGtm = true,
    inputName = '',
    inputId = '',
    eventAction = '',
    eventLabel = '',
    helpText,
    children,
    inputRef,
    setValue,
    clearErrors,
    backgroundColor,
    labelOnTop = false,
    onManualEntry,
    onAddressSelected,
    defaultLabel,
    isAddressFinder = false,
    className = ''
}) => {
    const {
        app: {commerceAPI}
    } = getConfig()
    const {configs: checkoutConfig} = commerceAPI.checkoutConfig || {}
    let enableLoqate = true
    try {
        if (checkoutConfig) {
            const parsedConfigs = JSON.parse(checkoutConfig)
            enableLoqate = parsedConfigs.enableLoqate
        }
    } catch (e) {
        console.error('Error in parsing checkoutConfig', e.message)
    }
    const styles = useStyleConfig('FormStyles')
    const [suggestions, setSuggestions] = useState({})
    const {getAddressSuggestion} = useLoqate()
    const tagManager = useTagManager()
    const [inputFocused, setInputFocused] = useState(false)
    // extend styles
    styles.inputField = {
        ...styles.inputField,
        backgroundColor: backgroundColor || (labelOnTop ? 'white' : 'transparent'),
        ...((labelOnTop || backgroundColor) && type !== 'select' && {padding: '12px 15px'})
    }
    const addressSuggestions = debounce(async (text) => {
        getAddressSuggestion({text: text}, setSuggestions)
    }, DEBOUNCE_WAIT)
    return (
        <Controller
            name={name}
            control={control}
            rules={rules}
            defaultValue={defaultValue}
            render={({field: {onChange, onBlur, value, ref}}) => {
                const _inputProps =
                    typeof inputProps === 'function' ? inputProps({value, onChange}) : inputProps
                const formLabelStyle = labelOnTop
                    ? {
                          position: 'static',
                          fontWeight: '300',
                          fontFamily: 'inter',
                          fontSize: '16px',
                          color: 'black',
                          marginBottom: '5px'
                      }
                    : type === 'select'
                    ? {
                          color: '#000000',
                          fontWeight: '400',
                          fontSize: '16px',
                          marginTop: '-6px',
                          marginBottom: '5px'
                      }
                    : {...styles.formLabel}
                return (
                    <FormControl
                        id={name}
                        className={`field--${name}`}
                        isInvalid={error}
                        {...styles.formControl}
                    >
                        {!['checkbox', 'radio', 'hidden'].includes(type) &&
                            (formLabel || (
                                <FormLabel
                                    {...(labelOnTop
                                        ? {}
                                        : {
                                              fontSize: value || inputFocused ? '13px' : '16px',
                                              top: value || inputFocused ? '1px' : '16px'
                                          })}
                                    {...formLabelStyle}
                                    {...(isAddressFinder && {
                                        left: '40px',
                                        fontSize: value || inputFocused ? '13px' : '16px',
                                        color: value || inputFocused ? 'gray.650' : 'black.100'
                                    })}
                                    pointerEvents="none"
                                >
                                    {defaultLabel && !value && !inputFocused ? defaultLabel : label}
                                </FormLabel>
                            ))}

                        <InputGroup {...styles.inputGroup}>
                            {['text', 'password', 'email', 'phone', 'tel', 'number'].includes(
                                type
                            ) && (
                                <Input
                                    ref={(node) => {
                                        ref(node)
                                        if (inputRef) inputRef.current = node
                                    }}
                                    onChange={(event) => {
                                        onChange(event)
                                        if (name === 'address1') {
                                            delete window.loqateAddress1
                                        }
                                        if (isAddressFinder) {
                                            if (enableLoqate) {
                                                window.setTimeout(() => {
                                                    addressSuggestions(event.target.value)
                                                }, 0)
                                            }
                                            setValue(
                                                'addressFinder',
                                                formatAddress(event.target.value)
                                            )
                                        }
                                        if (form?.trigger) {
                                            form.trigger(name)
                                        }
                                        window.isBillingAddressValidated = false
                                    }}
                                    value={value}
                                    name={eventLabel || name}
                                    type={type}
                                    placeholder={labelOnTop ? placeholder : ''}
                                    autoComplete={autoComplete}
                                    className={className}
                                    {..._inputProps}
                                    {...styles.inputField}
                                    {...(isAddressFinder ? {paddingLeft: '40px'} : {})}
                                    onFocus={() => {
                                        setInputFocused(true)
                                    }}
                                    onBlur={(e) => {
                                        onBlur()
                                        if (form?.trigger) {
                                            form.trigger(name)
                                        }
                                        setInputFocused(false)
                                        if (
                                            e.target.value &&
                                            eventAction &&
                                            eventLabel &&
                                            triggerGtm
                                        ) {
                                            tagManager.formFieldChange(eventAction, eventLabel)
                                        }
                                    }}
                                />
                            )}
                            {type === 'textarea' && (
                                <Textarea
                                    ref={(node) => {
                                        ref(node)
                                        if (inputRef) inputRef.current = node
                                    }}
                                    onChange={onChange}
                                    value={value}
                                    name={eventLabel || name}
                                    type="text"
                                    autoComplete={autoComplete}
                                    {..._inputProps}
                                    {...styles.inputField}
                                    onFocus={() => {
                                        setInputFocused(true)
                                    }}
                                    onBlur={(e) => {
                                        onBlur()
                                        setInputFocused(false)
                                        if (
                                            e.target.value &&
                                            eventAction &&
                                            eventLabel &&
                                            triggerGtm
                                        ) {
                                            tagManager.formFieldChange(eventAction, eventLabel)
                                        }
                                    }}
                                />
                            )}
                            {type === 'hidden' && (
                                <input
                                    ref={ref}
                                    onChange={onChange}
                                    value={value}
                                    type="hidden"
                                    {..._inputProps}
                                />
                            )}

                            {type === 'select' && (
                                <Select
                                    ref={ref}
                                    onChange={onChange}
                                    onBlur={onBlur}
                                    value={value}
                                    placeholder={placeholder}
                                    {..._inputProps}
                                    {...styles.inputField}
                                    icon={
                                        <Image
                                            alt="Arrow"
                                            src={getUrl('UIImages/images/down-arrow-new.svg')}
                                            {...styles.selectIcon}
                                            width="13px"
                                            height="13px"
                                            marginRight="12px"
                                        />
                                    }
                                    id={inputId || type}
                                    name={inputName || type}
                                >
                                    {options.map((opt) => (
                                        <option key={`${opt.label}-${opt.value}`} value={opt.value}>
                                            {opt.label}
                                        </option>
                                    ))}
                                </Select>
                            )}

                            {type === 'checkbox' && (
                                <Checkbox
                                    ref={ref}
                                    onChange={(e) => onChange(e.target.checked)}
                                    isChecked={value}
                                    {..._inputProps}
                                >
                                    {formLabel || label}
                                </Checkbox>
                            )}

                            {children}
                        </InputGroup>
                        {isAddressFinder && enableLoqate && (
                            <AddressSuggestions
                                suggestions={suggestions}
                                setSuggestions={setSuggestions}
                                setValue={setValue}
                                clearErrors={clearErrors}
                                onManualEntry={onManualEntry}
                                onAddressSelected={onAddressSelected}
                                form={form}
                            ></AddressSuggestions>
                        )}
                        {error && type !== 'hidden' && (
                            <FormErrorMessage {...styles.errorMessage}>
                                {error.message}
                            </FormErrorMessage>
                        )}

                        {helpText}
                    </FormControl>
                )
            }}
        />
    )
}

Field.propTypes = {
    name: PropTypes.string,
    label: PropTypes.string,
    autoComplete: PropTypes.string,
    formLabel: PropTypes.any,
    type: PropTypes.oneOf([
        'text',
        'number',
        'password',
        'email',
        'phone',
        'tel',
        'select',
        'checkbox',
        'hidden'
    ]),
    options: PropTypes.arrayOf(PropTypes.shape({label: PropTypes.string, value: PropTypes.any})),
    rules: PropTypes.object,
    error: PropTypes.shape({message: PropTypes.string}),
    placeholder: PropTypes.string,
    inputProps: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
    control: PropTypes.object,
    defaultValue: PropTypes.any,
    helpText: PropTypes.any,
    eventAction: PropTypes.string,
    inputId: PropTypes.string,
    inputName: PropTypes.string,
    eventLabel: PropTypes.string,
    children: PropTypes.any,
    triggerGtm: PropTypes.bool,
    inputRef: PropTypes.object,
    backgroundColor: PropTypes.string,
    setValue: PropTypes.func,
    clearErrors: PropTypes.any,
    labelOnTop: PropTypes.bool,
    onManualEntry: PropTypes.func,
    onAddressSelected: PropTypes.func,
    defaultLabel: PropTypes.string,
    className: PropTypes.string,
    form: PropTypes.any,
    isAddressFinder: PropTypes.bool
}

export default Field
